import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/public/Header";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("invalid"); setError("Missing invite token."); return; }
    api.get(`/auth/invite/${token}`)
      .then((r) => { setInvite(r.data); setState("ready"); })
      .catch((e) => { setState("invalid"); setError(e.response?.data?.detail || "This invite link is not valid."); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/invite/${token}/accept`, { password });
      localStorage.setItem("cz_token", data.token);
      toast.success("Welcome aboard! Taking you to the console…");
      setTimeout(() => { window.location.href = "/admin"; }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept the invite");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      <div className="hidden lg:flex flex-col justify-between bg-[#0b1a3a] text-white p-12">
        <Logo light />
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-tight">Join the<br />CoworkZen Team</h1>
          <p className="text-slate-400 mt-4 max-w-sm">Set your password to activate your admin account and get the access assigned to you.</p>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} CoworkZen</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="accept-invite">
          {state === "loading" && <p className="text-slate-500 text-sm" data-testid="invite-loading">Checking your invite…</p>}

          {state === "invalid" && (
            <div data-testid="invite-invalid">
              <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center mb-5"><AlertTriangle className="w-6 h-6" /></div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Invite unavailable</h2>
              <p className="text-sm text-slate-500 mt-2">{error}</p>
              <a href="/admin/login" className="text-primary font-semibold text-sm mt-5 inline-block hover:underline">Go to sign in</a>
            </div>
          )}

          {state === "ready" && (
            <form onSubmit={submit} data-testid="invite-form">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center mb-5"><CheckCircle2 className="w-6 h-6" /></div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Set your password</h2>
              <p className="text-sm text-slate-500 mt-1 mb-6">Invited as <span className="font-semibold text-slate-700">{invite?.email}</span></p>
              <label className="text-sm font-medium text-slate-700">New password</label>
              <Input data-testid="invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className="mt-1.5 mb-4 h-11" />
              <label className="text-sm font-medium text-slate-700">Confirm password</label>
              <Input data-testid="invite-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" className="mt-1.5 mb-6 h-11" />
              <Button data-testid="invite-submit" type="submit" disabled={busy} className="w-full h-11 rounded-full gap-2">
                {busy ? "Activating…" : <>Activate account <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
